import type {
  Artist,
  Booking,
  CustomerRecord,
  DayOfWeek,
  Deposit,
  Piece,
  RebookFollowupTask,
  HealingCheckTask,
  StudioId,
  TopupPlan,
} from '@/domain/types';
import { recommendChannel, type ChannelRecommendation } from '@/domain/channelRecommendation';
import type { RedKind, SequenceStage, SequenceState } from '@/domain/sequenceFlow';
import { relativeFromIso } from '@/utils/format';

export type QueueBand = 'now' | 'today' | 'gap';

export type QueueKind =
  | 'deposit-on-file'
  | 'callback-firing'
  | 'callback-today'
  | 'pending-deposit'
  | 'missed-call'
  | 'unread-message'
  | 'pushed-lead'
  | 'missed-topup'
  | 'unfinished-piece'
  | 'rebook-followup'
  | 'healing-check'
  | 'gap-candidate';

/** Which lane of the operator queue a kind belongs to. See deriveCohort. */
export type QueueCohort = 'fresh' | 'money' | 'open-business';

/**
 * The five-rung fill ladder a gap-fill candidate sorts into, worked left to
 * right (board 08). Set on gap-candidate items by buildGapQueue; drives the
 * Sweeper's rung tabs. Rung 3 keeps the legacy value 'past-client' but is
 * broadened to open/unfinished business (incl. no-rebook mid-piece) and labelled
 * "Open business" in the UI.
 *   1 bring-forward · 2 deposit · 3 past-client (open business)
 *   4 expired (expired links & never-paid) · 5 no-show (forfeits & no-shows)
 */
export type GapBucket = 'bring-forward' | 'deposit' | 'past-client' | 'expired' | 'no-show';

export type QueueChannel = 'voice' | 'sms' | 'meta-dm' | 'ig-dm' | 'email';

export interface MatchEvidence {
  kind:
    | 'unfinished-piece'
    | 'style-match'
    | 'previous-booking'
    | 'spend-tier'
    | 'engagement'
    | 'future-signal'
    | 'mentioned-artist'
    | 'calendar-open'
    | 'calendar-busy'
    | 'proximity'
    | 'tier'
    | 'wait'
    | 'sequence'
    | 'deposit-age'
    | 'days-since-contact'
    | 'channel-choice';
  weight: number;
  text: string;
}

export interface QueueItem {
  id: string;
  kind: QueueKind;
  band: QueueBand;
  customer: CustomerRecord;
  reason: string;
  detail?: string;
  recommendedChannel: QueueChannel;
  channelReason: string;
  channelConfidence: ChannelRecommendation['confidence'];
  recommendedAt?: string;
  priority: number;
  matchScore?: number;
  matchReason?: string;
  evidence?: MatchEvidence[];
  depositId?: string;
  rebookFollowupTaskId?: string;
  /**
   * For a missed-topup item: the TopupPlan the Agent's 3-sequence chase acts on.
   * The card's chase-outcome buttons pass it straight to the topupChase* engine.
   */
  topupPlanId?: string;
  /** Which queue lane this item sorts into. Undefined for rail-only kinds. */
  cohort?: QueueCohort;
  /** Sequence-clock state for this customer, when one exists. */
  stage?: SequenceStage;
  redKind?: RedKind;
  /** True while the sequence's nextDueAt is still in the future. */
  dormant?: boolean;
  sequenceDueAtIso?: string;
  /** For gap-fill candidates: which rung of the five-rung ladder this sorts into. */
  gapBucket?: GapBucket;
  /**
   * For gap candidates on the deposit + open-business rungs only: days since the
   * client's most recent completed tattoo, when they have one. Drives the subtle
   * "Tattooed N days ago" flag (bd tv-25a). No cut-off — the day count is the
   * whole mechanism; the sweeper decides case by case.
   */
  recentlyTattooedDays?: number;
  /**
   * For bring-forward (rung 1) candidates: the client asked to be brought forward
   * if an earlier slot opens (`Booking.preferBroughtForward`). Always sorts to the
   * top of the rung automatically and drives the "brought forward on request"
   * badge on the candidate row (bd tv-dme, board 08).
   */
  preferBroughtForward?: boolean;
  /**
   * For bring-forward (rung 1) candidates: the ISO start of the future booking
   * that could jump the line. Lets the rung's sort control order the non-preferred
   * remainder by date (soonest vs furthest-out) without re-deriving it (tv-dme).
   */
  bringForwardBookingIso?: string;
  /**
   * For pushed leads with a chosen callback time: the ISO instant. Its presence
   * both pins the card at its slot (via recommendedAt) and mirrors it onto the
   * Scheduled-calls rail.
   */
  pinnedCallbackAtIso?: string;
  /**
   * True for a pending-deposit item generated from a recently-expired,
   * non-superseded deposit link (see buildStandardQueue). Drives the "resend"
   * detail copy here and the resend action in QueueWorkModal.
   */
  depositExpired?: boolean;
  /**
   * The agent who booked/secured this item, when known (money cohort: from the
   * deposit's takenByAgentId). Display + attribution only; does not scope the
   * queue.
   */
  ownerAgentId?: string;
}

export interface GapFilter {
  artistId: string;
  dayIso: string;
}

interface BuildArgs {
  customers: Record<string, CustomerRecord>;
  bookings: Record<string, Booking>;
  deposits: Record<string, Deposit>;
  pieces: Record<string, Piece>;
  artists: Record<string, Artist>;
  /** Sales-team follow-up tasks from Outcome C — surfaced as a queue kind. */
  rebookFollowupTasks?: Record<string, RebookFollowupTask>;
  healingCheckTasks?: Record<string, HealingCheckTask>;
  /** Pay-over-time plans; a missed/overdue instalment feeds the Agent chase. */
  topupPlans?: Record<string, TopupPlan>;
  studioIds: StudioId[];
  nowIso: string;
  gapFilter?: GapFilter;
  /** Per-customer sequence-clock state, keyed by customer id. */
  sequenceState?: Record<string, SequenceState>;
}

const HOUR = 60 * 60 * 1000;

export function buildQueue(args: BuildArgs): QueueItem[] {
  return args.gapFilter ? buildGapQueue(args, args.gapFilter) : buildStandardQueue(args);
}

function buildStandardQueue(args: BuildArgs): QueueItem[] {
  const allow = new Set(args.studioIds);
  const inStudio = (id: StudioId) => allow.size === 0 || allow.has(id);
  const now = new Date(args.nowIso).getTime();
  const items: QueueItem[] = [];

  for (const c of Object.values(args.customers)) {
    if (!inStudio(c.primaryStudioId)) continue;

    for (const sig of c.futureReadinessSignals) {
      if (sig.status !== 'scheduled') continue;
      const at = new Date(sig.scheduledFor).getTime();
      const diff = at - now;
      if (diff < -HOUR * 2 || diff > HOUR * 10) continue;
      // Pop into the top of the queue 15 minutes before the scheduled time.
      const firing = diff <= HOUR * 0.25;
      const sigChannel = castChannel(sig.recommendedChannel);
      items.push({
        id: `q-frs-${sig.id}`,
        kind: firing ? 'callback-firing' : 'callback-today',
        band: firing ? 'now' : 'today',
        customer: c,
        reason: firing ? 'Callback now' : 'Callback later today',
        detail: sig.rawPhrase,
        recommendedChannel: sigChannel,
        channelReason: `customer asked for ${labelChannel(sigChannel)} contact`,
        channelConfidence: 'high',
        recommendedAt: sig.scheduledFor,
        priority: firing ? 1000 - Math.abs(diff) / 60_000 : 700 - diff / 60_000,
        evidence: contextEvidence(c, [
          { kind: 'future-signal', weight: 25, text: `Captured phrase: "${sig.rawPhrase}"` },
        ]),
      });
    }
  }

  for (const d of Object.values(args.deposits)) {
    if (d.status !== 'paid' || d.bookingId) continue;
    if (!inStudio(d.studioId)) continue;
    const c = args.customers[d.customerId];
    if (!c) continue;
    const paidMs = d.paidAt ? new Date(d.paidAt).getTime() : undefined;
    const paidAgoMs = paidMs != null ? now - paidMs : 0;
    const rec = recommendChannel(c);
    items.push({
      id: `q-depfile-${d.id}`,
      kind: 'deposit-on-file',
      band: 'today',
      customer: c,
      reason: 'Deposit on file, no date booked',
      detail: `$${d.amountAud} deposit paid ${formatHours(paidAgoMs)} ago, still no date booked.`,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      recommendedAt: d.paidAt,
      priority: 500,
      depositId: d.id,
      ...(d.takenByAgentId ? { ownerAgentId: d.takenByAgentId } : {}),
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: 40,
          text: `Deposit on file: $${d.amountAud} paid ${formatHours(paidAgoMs)} ago, no booking yet`,
        },
      ]),
    });
  }

  for (const d of Object.values(args.deposits)) {
    if (d.status !== 'pending') continue;
    if (!inStudio(d.studioId)) continue;
    const c = args.customers[d.customerId];
    if (!c) continue;
    const ms = new Date(d.expiresAt).getTime() - now;
    if (ms < 0 || ms > HOUR * 48) continue;
    const expiringSoon = ms <= HOUR * 4;
    const rec = recommendChannel(c);
    items.push({
      id: `q-dep-${d.id}`,
      kind: 'pending-deposit',
      band: expiringSoon ? 'now' : 'today',
      customer: c,
      reason: expiringSoon ? 'Deposit expires soon' : 'Deposit pending',
      detail: `$${d.amountAud} · expires in ${formatHours(ms)}`,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      recommendedAt: d.expiresAt,
      priority: expiringSoon ? 900 : 500,
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: expiringSoon ? 30 : 15,
          text: `$${d.amountAud} ${labelChannel(castChannel(d.channelSent))} link sent ${formatHours(now - new Date(d.createdAt).getTime())} ago, expires in ${formatHours(ms)}`,
        },
      ]),
    });
  }

  // Recently-expired, un-resent deposit links also surface under Awaiting
  // deposit (still `pending-deposit`, still `money` cohort) so the agent can
  // catch and resend them before the lead goes cold. Superseded (already
  // resent) or stale (>7d expired) links don't surface.
  for (const d of Object.values(args.deposits)) {
    if (d.status !== 'expired' || d.supersededByDepositId) continue;
    if (!inStudio(d.studioId)) continue;
    const c = args.customers[d.customerId];
    if (!c) continue;
    const expiredAgoMs = now - new Date(d.expiresAt).getTime();
    if (expiredAgoMs < 0 || expiredAgoMs > 7 * 24 * HOUR) continue;
    const rec = recommendChannel(c);
    items.push({
      id: `q-dep-${d.id}`,
      kind: 'pending-deposit',
      band: 'today',
      customer: c,
      reason: 'Deposit link expired',
      detail: `$${d.amountAud} · Deposit link expired — resend`,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      recommendedAt: d.expiresAt,
      priority: 500,
      depositId: d.id,
      depositExpired: true,
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: 15,
          text: `$${d.amountAud} deposit link expired ${formatHours(expiredAgoMs)} ago — resend`,
        },
      ]),
    });
  }

  for (const c of Object.values(args.customers)) {
    if (!inStudio(c.primaryStudioId)) continue;
    const lastVoiceIn = lastMatching(
      c.conversation,
      (e) => e.channel === 'voice' && e.direction === 'in',
    );
    if (lastVoiceIn) {
      const t = new Date(lastVoiceIn.timestamp).getTime();
      // 72h window so overnight/weekend voicemails persist into Monday's queue.
      if (now - t <= HOUR * 72) {
        const calledBack = c.conversation.some(
          (e) =>
            e.channel === 'voice' &&
            e.direction === 'out' &&
            new Date(e.timestamp).getTime() > t,
        );
        if (!calledBack) {
          items.push({
            id: `q-mc-${c.id}-${lastVoiceIn.id}`,
            kind: 'missed-call',
            band: 'today',
            customer: c,
            reason: 'Missed call',
            detail: lastVoiceIn.body.slice(0, 90),
            recommendedChannel: 'voice',
            channelReason: 'return their call',
            channelConfidence: 'high',
            recommendedAt: lastVoiceIn.timestamp,
            priority: 600 - (now - t) / 60_000,
            evidence: contextEvidence(c, [
              {
                kind: 'future-signal',
                weight: 18,
                text: `Voicemail: "${lastVoiceIn.body.slice(0, 120)}"`,
              },
            ]),
          });
        }
      }
    }

    const lastInboundMsg = lastMatching(
      c.conversation,
      (e) => e.direction === 'in' && e.channel !== 'voice',
    );
    if (lastInboundMsg) {
      const t = new Date(lastInboundMsg.timestamp).getTime();
      if (now - t <= HOUR * 6) {
        const replied = c.conversation.some(
          (e) => e.direction === 'out' && new Date(e.timestamp).getTime() > t,
        );
        if (!replied) {
          const inboundCh = castChannel(lastInboundMsg.channel);
          items.push({
            id: `q-msg-${lastInboundMsg.id}`,
            kind: 'unread-message',
            band: 'today',
            customer: c,
            reason: 'Unread message',
            detail: lastInboundMsg.body.slice(0, 90),
            recommendedChannel: inboundCh,
            channelReason: `they messaged you on ${labelChannel(inboundCh)}`,
            channelConfidence: 'high',
            recommendedAt: lastInboundMsg.timestamp,
            priority: 550 - (now - t) / 60_000,
            evidence: contextEvidence(c, [
              {
                kind: 'future-signal',
                weight: 15,
                text: `"${lastInboundMsg.body.slice(0, 120)}"`,
              },
            ]),
          });
        }
      }
    }
  }

  for (const p of Object.values(args.pieces)) {
    if (p.status !== 'in-progress' || p.nextPlannedSession) continue;
    const c = args.customers[p.customerId];
    if (!c || !inStudio(c.primaryStudioId)) continue;
    const rec = recommendChannel(c);
    const remaining = Math.max(0, p.estimatedTotalHours - p.hoursCompleted);
    items.push({
      id: `q-up-${p.id}`,
      kind: 'unfinished-piece',
      band: 'today',
      customer: c,
      reason: 'Unfinished piece',
      detail: `${p.designType} · ${p.placement} · ${p.hoursCompleted}/${p.estimatedTotalHours}h`,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      priority: 300,
      evidence: contextEvidence(c, [
        {
          kind: 'unfinished-piece',
          weight: 20,
          text: `${p.title || p.designType.replace('-', ' ')} on ${p.placement} · ~${remaining}h left of ${p.estimatedTotalHours}h`,
        },
        ...(p.lastSessionDate
          ? [
              {
                kind: 'future-signal' as const,
                weight: 8,
                text: `Last session ${formatHours(Date.now() - new Date(p.lastSessionDate).getTime())} ago`,
              },
            ]
          : []),
      ]),
    });
  }

  // Sales-team rebook follow-ups (Outcome C). Surfaced as a queue item until
  // resolved. Higher priority while fresh (<48h), then decays.
  for (const task of Object.values(args.rebookFollowupTasks ?? {})) {
    if (task.resolvedAt) continue;
    if (!inStudio(task.studioId)) continue;
    const c = args.customers[task.customerId];
    if (!c) continue;
    const created = new Date(task.createdAt).getTime();
    const ageHrs = Math.max(0, (now - created) / HOUR);
    const rec = recommendChannel(c);
    const stale = ageHrs > 5 * 24;
    items.push({
      id: `q-rebookfu-${task.id}`,
      kind: 'rebook-followup',
      rebookFollowupTaskId: task.id,
      band: ageHrs < 48 ? 'today' : 'gap',
      customer: c,
      reason: 'Rebook follow-up',
      detail: task.note
        ? task.note.slice(0, 90)
        : `Floor team deferred to sales after ${args.artists[task.originalArtistId]?.firstName ?? 'their artist'}'s session.`,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      recommendedAt: task.createdAt,
      priority: Math.max(380, 520 - ageHrs * 2) + (stale ? -30 : 0),
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: 16,
          text: `Deferred from chair · ${args.artists[task.originalArtistId]?.firstName ?? 'artist'} · ${stale ? `stale ${Math.floor(ageHrs / 24)}d` : `${Math.floor(ageHrs)}h ago`}`,
        },
      ]),
    });
  }

  // Healing-check nudges (09 Jul). A completed session that didn't rebook in the
  // chair surfaces 7 days later ("how's it healing?" + line up the next piece).
  // Money cohort when a deposit still sits on the piece (set on the item below).
  for (const task of Object.values(args.healingCheckTasks ?? {})) {
    if (task.resolvedAt) continue;
    if (new Date(task.dueAt).getTime() > now) continue; // not due until T+7d
    if (!inStudio(task.studioId)) continue;
    const c = args.customers[task.customerId];
    if (!c) continue;
    const rec = recommendChannel(c);
    const daysAgo = Math.floor((now - new Date(task.createdAt).getTime()) / (24 * HOUR));
    items.push({
      id: `q-healing-${task.id}`,
      kind: 'healing-check',
      band: 'today',
      cohort: task.depositId ? 'money' : undefined,
      customer: c,
      reason: 'Healing check',
      detail: "How's the tattoo healing? Check in and line up the next piece.",
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      recommendedAt: task.dueAt,
      priority: 360,
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: 14,
          text: `Session ${daysAgo}d ago · healing check due`,
        },
      ]),
    });
  }

  // Manually pushed leads (Prompt C). A sequence record with a sourceConversationId
  // is a lead a messager handed to the booking team. Mint a card for it even when the
  // customer has no other signal. Low priority so the per-customer dedup below keeps
  // any real signal instead - this is exactly the "no higher-priority item" rule.
  for (const [customerId, seq] of Object.entries(args.sequenceState ?? {})) {
    if (!seq.sourceConversationId) continue;
    const c = args.customers[customerId];
    if (!c || !inStudio(c.primaryStudioId)) continue;
    const rec = recommendChannel(c);
    const lastMsg = c.conversation[c.conversation.length - 1];
    // FIFO position: pin at the callback slot when set, else the last message time,
    // else now - so a pushed lead sits among fresh inquiries by when it arrived.
    const recommendedAt = seq.pinnedCallbackAt ?? lastMsg?.timestamp ?? args.nowIso;
    // Expired-link resend routed to a taken date (Prompt I1): lands at the TOP of
    // the money band (priority above the 900 expiring-soon max), attributed to the
    // agent who took the deposit, carrying the deposit ref.
    const moneyResend = seq.cohort === 'money' && Boolean(seq.depositId);
    const resendDep = seq.depositId ? args.deposits[seq.depositId] : undefined;
    items.push({
      id: `q-push-${c.id}`,
      kind: 'pushed-lead',
      band: 'today',
      customer: c,
      reason: moneyResend ? 'Deposit needs resend - date is filled' : 'Pushed from Conversations',
      detail: seq.description ? seq.description.slice(0, 90) : undefined,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      recommendedAt,
      priority: moneyResend ? 990 : 100,
      pinnedCallbackAtIso: seq.pinnedCallbackAt,
      ...(moneyResend ? { cohort: 'money' as const, depositId: seq.depositId } : {}),
      ...(moneyResend && resendDep?.takenByAgentId
        ? { ownerAgentId: resendDep.takenByAgentId }
        : {}),
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: 14,
          text: seq.description
            ? `Pushed by messaging - ${seq.description.slice(0, 100)}`
            : 'Pushed by messaging for a booking agent',
        },
      ]),
    });
  }

  // Missed top-up chase (tv-9vr, Diagram 05). A pay-over-time plan with a missed
  // instalment feeds the Agent worklist as a fresh, oldest-first chase item: the
  // Agent runs the 3-sequence retention chase (prefer chasing to refilling)
  // rather than the Sweeper refilling the spot. The chase ENGINE already exists
  // (topupChaseFlow); this only surfaces the plan so its card can call it.
  for (const plan of Object.values(args.topupPlans ?? {})) {
    if (plan.status !== 'active') continue;
    if (!inStudio(plan.studioId)) continue;
    const c = args.customers[plan.customerId];
    if (!c) continue;
    const missed = plan.instalments.find((i) => i.status === 'missed');
    if (!missed) continue;
    const overdueMs = Math.max(0, now - new Date(missed.dueIso).getTime());
    const attempts = plan.chaseAttempts ?? 0;
    items.push({
      id: `q-topup-${plan.id}`,
      kind: 'missed-topup',
      band: 'today',
      customer: c,
      reason: 'Missed top-up, chase to retain',
      detail: `$${missed.amountAud} instalment missed ${formatHours(overdueMs)} ago${
        attempts > 0 ? ` · ${attempts}/3 sequences` : ''
      }.`,
      recommendedChannel: 'voice',
      channelReason: 'call to retain the booking before refilling the spot',
      channelConfidence: 'high',
      // Oldest-first: pin at the missed instalment's due date so the fresh tier
      // FIFO ordering surfaces the longest-overdue chase first.
      recommendedAt: missed.dueIso,
      priority: 640,
      topupPlanId: plan.id,
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: 22,
          text: `Missed $${missed.amountAud} top-up ${formatHours(overdueMs)} ago · booking still held, chase to keep it`,
        },
      ]),
    });
  }

  // Stamp cohort and any sequence-clock state onto every item before ordering.
  for (const item of items) {
    // Respect a cohort the minter set explicitly (e.g. a healing-check with a
    // deposit still on the piece → money lane); otherwise derive from the kind.
    item.cohort = item.cohort ?? deriveCohort(item.kind);
    const seq = args.sequenceState?.[item.customer.id];
    if (seq) {
      item.stage = seq.stage;
      item.redKind = seq.redKind;
      item.sequenceDueAtIso = seq.nextDueAt;
      item.dormant = seq.nextDueAt != null && new Date(seq.nextDueAt).getTime() > now;
      // A pinned callback lives on the sequence record, so stamp it onto whatever
      // item represents this customer - not just the pushed-lead card. Otherwise a
      // higher-priority signal (e.g. an unread message) wins the dedup below and the
      // callback pin, and its rail row, would be lost.
      if (seq.pinnedCallbackAt) {
        item.pinnedCallbackAtIso = seq.pinnedCallbackAt;
        item.recommendedAt = seq.pinnedCallbackAt;
      }
    }
    // Main-list items (those with a cohort) get position-justifying bullets so
    // their "Why this matters" expander explains where they sit and why. Append
    // to preserve the context evidence already stamped above.
    if (item.cohort) {
      item.evidence = [...(item.evidence ?? []), ...positionEvidence(item, args.nowIso)];
    }
  }

  // Keep highest-priority entry per customer so they don't appear twice.
  const dedup = new Map<string, QueueItem>();
  for (const item of items) {
    const prev = dedup.get(item.customer.id);
    if (!prev || item.priority > prev.priority) dedup.set(item.customer.id, item);
  }
  return [...dedup.values()].sort(compareByTier);
}

/**
 * Maps a queue kind to its cohort lane. Scheduled-call and gap kinds
 * (callback-firing, callback-today, gap-candidate) have no cohort and are
 * filtered out of the main list by consumers.
 */
export function deriveCohort(kind: QueueKind): QueueCohort | undefined {
  switch (kind) {
    case 'unread-message':
    case 'missed-call':
    case 'pushed-lead':
    case 'missed-topup':
      return 'fresh';
    case 'pending-deposit':
    case 'deposit-on-file':
      return 'money';
    case 'unfinished-piece':
    case 'rebook-followup':
    case 'healing-check':
      return 'open-business';
    case 'callback-firing':
    case 'callback-today':
    case 'gap-candidate':
      return undefined;
  }
}

/** Which role-based queue lane a kind belongs to. See laneOf. */
export type QueueLane = 'agent' | 'sweeper';

/**
 * Splits the shared queue by role (bd tv-570). The booking Agent works fresh
 * inquiries, scheduled callbacks and gap-fill; the Sweeper works the money and
 * open-business tail (deposits, unfinished pieces, rebooks, healing checks).
 * Pure classifier over the kind: the engine keeps building one full queue and
 * each screen filters to its own lane.
 */
export function laneOf(kind: QueueKind): QueueLane {
  switch (kind) {
    case 'unread-message':
    case 'missed-call':
    case 'pushed-lead':
    case 'missed-topup':
    case 'callback-firing':
    case 'callback-today':
    case 'gap-candidate':
      return 'agent';
    case 'deposit-on-file':
    case 'pending-deposit':
    case 'unfinished-piece':
    case 'rebook-followup':
    case 'healing-check':
      return 'sweeper';
  }
}

/**
 * The ordered channels whose action buttons a card should render (the card maps
 * 1:1 over this list). Always leads with the recommended channel, optionally
 * offers a voice dial when there's a phone, then at most one message channel the
 * customer is genuinely reachable on. Dark-red said-no items get no channel
 * buttons.
 */
export function queueActionsFor(item: QueueItem): QueueChannel[] {
  if (item.redKind === 'said-no') return [];

  const out: QueueChannel[] = [item.recommendedChannel];
  if (item.customer.phone && item.recommendedChannel !== 'voice') out.push('voice');
  const msg = bestMessageChannel(item.customer);
  if (msg && !out.includes(msg)) out.push(msg);
  return out;
}

// The single best message channel the customer is genuinely reachable on, in a
// fixed preference order. Reachability: sms needs a phone; meta-dm needs a prior
// Messenger conversation entry; ig-dm needs a handle; email needs an address.
function bestMessageChannel(customer: CustomerRecord): QueueChannel | undefined {
  const reachable: Record<Exclude<QueueChannel, 'voice'>, boolean> = {
    sms: Boolean(customer.phone),
    'meta-dm': customer.conversation.some((e) => e.channel === 'meta-dm'),
    'ig-dm': Boolean(customer.instagramHandle),
    email: Boolean(customer.email),
  };
  const order: Array<Exclude<QueueChannel, 'voice'>> = ['sms', 'meta-dm', 'ig-dm', 'email'];
  return order.find((ch) => reachable[ch]);
}

/**
 * One evidence bullet per real ranking signal behind an item's position, in the
 * shared { kind, weight, text } shape with modest POSITIVE weights so the card
 * renders them as "+N", not red. These are cosmetic labels, not scoring inputs.
 * The wait bullet's weight uses the passed nowIso (never Date.now()), while its
 * display text uses relativeFromIso, which reads the global sim clock.
 */
export function positionEvidence(item: QueueItem, nowIso: string): MatchEvidence[] {
  const out: MatchEvidence[] = [];

  if (item.cohort === 'fresh') {
    out.push({
      kind: 'tier',
      weight: 6,
      text: 'Fresh inquiry - worked before deposits and rebooks',
    });
  } else if (item.cohort === 'money') {
    out.push({
      kind: 'tier',
      weight: 6,
      text: 'Deposit received, no date locked in - after fresh, before rebooks',
    });
  } else if (item.cohort === 'open-business') {
    out.push({
      kind: 'tier',
      weight: 6,
      text: 'Open business - rebooks and unfinished work last',
    });
  }

  if (item.recommendedAt) {
    const ageMs = Math.max(0, new Date(nowIso).getTime() - new Date(item.recommendedAt).getTime());
    const waited = relativeFromIso(item.recommendedAt).replace(/ ago$/, '').replace(/^in /, '');
    out.push({
      kind: 'wait',
      weight: Math.min(12, 2 + Math.round(ageMs / (6 * HOUR))),
      text: `Waiting ${waited} - older items rise`,
    });
  }

  if (item.stage != null) {
    if (item.stage === 'red') {
      // Two flavours of red reach the list: never-reached (no answer after the
      // full attempt ladder) and said-no (declined, now on automated outreach).
      // They must read differently - said-no did answer.
      out.push({
        kind: 'sequence',
        weight: 4,
        text:
          item.redKind === 'said-no'
            ? 'Declined - handled by automated outreach'
            : 'No answer after 3 attempts',
      });
    } else {
      out.push({
        kind: 'sequence',
        weight: 4,
        text: `Sequence stage ${item.stage} - attempt ${item.stage}`,
      });
    }
  }

  out.push({
    kind: 'channel-choice',
    weight: 4,
    text: `${labelChannel(item.recommendedChannel)} chosen - ${item.channelReason}`,
  });

  return out;
}

// Tier 0 pops scheduled callbacks that are due now to the very top; then the
// three cohort lanes; rail-only kinds sink to the bottom (consumers filter them
// out of the main list, so their exact order does not matter).
function tierOf(item: QueueItem): number {
  if (item.kind === 'callback-firing') return 0;
  if (item.cohort === 'fresh') return 1;
  if (item.cohort === 'money') return 2;
  if (item.cohort === 'open-business') return 3;
  return 9;
}

// Missing recommendedAt sorts last. We use a large-but-FINITE sentinel (not
// Infinity) so two missing values subtract to 0 instead of NaN, keeping the
// fresh-tier comparator total and the sort stable for any future kind.
const NO_RECOMMENDED_AT = Number.MAX_SAFE_INTEGER;

function recommendedAtMs(item: QueueItem): number {
  return item.recommendedAt ? new Date(item.recommendedAt).getTime() : NO_RECOMMENDED_AT;
}

/**
 * Tier comparator for the standard queue. Dormant rows sink to the absolute
 * bottom across every tier (A9); among non-dormant rows, orders by tier, then:
 * fresh sorts FIFO oldest-first by recommendedAt ascending; every other tier
 * keeps highest-priority-first.
 */
export function compareByTier(a: QueueItem, b: QueueItem): number {
  // Dormant rows sink to the absolute bottom, across all tiers (A9).
  const da = a.dormant ? 1 : 0;
  const db = b.dormant ? 1 : 0;
  if (da !== db) return da - db;
  const ta = tierOf(a);
  const tb = tierOf(b);
  if (ta !== tb) return ta - tb;
  if (ta === 1) return recommendedAtMs(a) - recommendedAtMs(b);
  return b.priority - a.priority;
}

/**
 * Splits an already-ordered standard queue into the sections the operator
 * screen renders. Incoming order is preserved within each bucket (scheduledCalls
 * is additionally ordered oldest-first by recommendedAt for the rail).
 *
 * When `nowIso` is provided, the scheduled-calls rail is constrained to calls due
 * within the next 24h — callback-today and pinned-callback mirrors further out
 * (some are days away) are dropped from the rail. Omitting `nowIso` applies no
 * time filter, preserving existing callers.
 */
export function partitionQueue(
  items: QueueItem[],
  nowIso?: string,
): {
  scheduledDue: QueueItem[];
  main: QueueItem[];
  scheduledCalls: QueueItem[];
} {
  const scheduledDue: QueueItem[] = [];
  let scheduledCalls: QueueItem[] = [];
  const main: QueueItem[] = [];
  for (const item of items) {
    if (item.kind === 'callback-firing') scheduledDue.push(item);
    else if (item.kind === 'callback-today') scheduledCalls.push(item);
    else if (item.cohort) {
      main.push(item);
      // A pushed lead with a chosen callback is worked in place (main list, pinned
      // at its slot) AND mirrored onto the Scheduled-calls rail so the fixed time
      // point is visible there too.
      if (item.pinnedCallbackAtIso) scheduledCalls.push(item);
    }
  }
  scheduledCalls.sort((a, b) => recommendedAtMs(a) - recommendedAtMs(b));
  if (nowIso != null) {
    const cutoff = new Date(nowIso).getTime() + 24 * HOUR;
    scheduledCalls = scheduledCalls.filter((item) => recommendedAtMs(item) <= cutoff);
  }
  return { scheduledDue, main, scheduledCalls };
}

const DAY_MS = 24 * HOUR;
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function gapShortDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTH_ABBR[d.getMonth()]}`;
}

/** Most recent conversation timestamp (ms), or null when never contacted. */
function lastContactMs(c: CustomerRecord): number | null {
  let latest: number | null = null;
  for (const entry of c.conversation) {
    const t = new Date(entry.timestamp).getTime();
    if (latest === null || t > latest) latest = t;
  }
  return latest;
}

/**
 * Baseline match signals shared by every gap bucket — the existing ranker
 * weights (previous bookings, unfinished piece, style, mention, spend,
 * future-readiness, engagement, proximity, calendar). Bucket-specific signals
 * (deposit age, days-since-contact, sequence stage) are layered on top.
 */
function gapBaseSignals(
  c: CustomerRecord,
  artist: Artist,
  args: BuildArgs,
  gapDayStart: number,
  custBookings: Booking[],
  custPieces: Piece[],
): { score: number; evidence: MatchEvidence[] } {
  const evidence: MatchEvidence[] = [];
  let score = 5;

  const prevWithArtist = custBookings.filter((b) => b.artistId === artist.id);
  if (prevWithArtist.length > 0) {
    const w = 35 + Math.min(20, prevWithArtist.length * 5);
    score += w;
    evidence.push({
      kind: 'previous-booking',
      weight: w,
      text: `Booked ${artist.displayName} ${prevWithArtist.length} time${prevWithArtist.length > 1 ? 's' : ''} before`,
    });
  }

  const unfinished = custPieces.find(
    (p) => p.assignedArtistId === artist.id && p.status === 'in-progress',
  );
  if (unfinished) {
    const remaining = Math.max(0, unfinished.estimatedTotalHours - unfinished.hoursCompleted);
    score += 45;
    evidence.push({
      kind: 'unfinished-piece',
      weight: 45,
      text: `Unfinished ${unfinished.designType}: ${unfinished.hoursCompleted}/${unfinished.estimatedTotalHours}h done, ~${remaining}h left`,
    });
  }

  if (c.designIntent && artist.styleSpeciality.includes(c.designIntent.type)) {
    score += 22;
    evidence.push({
      kind: 'style-match',
      weight: 22,
      text: `${c.designIntent.type.replace('-', ' ')} matches ${artist.firstName}'s speciality`,
    });
  }

  const firstName = artist.firstName.toLowerCase();
  const handle = artist.instagramHandle.toLowerCase().replace('@', '');
  const mentionHit = c.conversation.find((m) => {
    const body = m.body.toLowerCase();
    return body.includes(firstName) || (handle && body.includes(handle));
  });
  if (mentionHit) {
    score += 30;
    evidence.push({ kind: 'mentioned-artist', weight: 30, text: `Mentioned ${artist.firstName} in a message` });
  }

  if (c.lifetimeSpendAud >= 5000) {
    score += 20;
    evidence.push({ kind: 'spend-tier', weight: 20, text: `VIP spend: $${c.lifetimeSpendAud.toLocaleString()} lifetime` });
  } else if (c.lifetimeSpendAud >= 2000) {
    score += 12;
    evidence.push({ kind: 'spend-tier', weight: 12, text: `High spend: $${c.lifetimeSpendAud.toLocaleString()} lifetime` });
  } else if (c.lifetimeSpendAud >= 500) {
    score += 6;
    evidence.push({ kind: 'spend-tier', weight: 6, text: `Returning customer: $${c.lifetimeSpendAud.toLocaleString()} lifetime` });
  }

  const scheduledSignal = c.futureReadinessSignals.find((s) => s.status === 'scheduled');
  if (scheduledSignal) {
    score += 18;
    evidence.push({ kind: 'future-signal', weight: 18, text: `Future-readiness: "${scheduledSignal.rawPhrase}"` });
  }

  if (c.engagementStatus === 'lapsed') {
    score += 8;
    evidence.push({ kind: 'engagement', weight: 8, text: 'Lapsed (not seen in months), reactivation lift' });
  } else if (c.engagementStatus === 'dormant') {
    score += 12;
    evidence.push({ kind: 'engagement', weight: 12, text: 'Dormant: long overdue, high re-engagement value' });
  }

  if (c.distanceToNearestStudioKm <= 5) {
    score += 6;
    evidence.push({ kind: 'proximity', weight: 6, text: `${c.distanceToNearestStudioKm.toFixed(1)} km from studio` });
  }

  const customerBookings = c.bookings
    .map((id) => args.bookings[id])
    .filter((b): b is NonNullable<typeof b> => Boolean(b))
    .filter((b) => b.status !== 'cancelled' && b.status !== 'no-show' && b.status !== 'superseded');
  const conflictBooking = customerBookings.find((b) => {
    const bt = new Date(b.start).getTime();
    return Math.abs(bt - gapDayStart) <= 3 * DAY_MS;
  });
  const upcomingInWindow = customerBookings.filter((b) => {
    const bt = new Date(b.start).getTime();
    return bt >= gapDayStart && bt <= gapDayStart + 14 * DAY_MS;
  });
  if (conflictBooking) {
    score -= 18;
    evidence.push({ kind: 'calendar-busy', weight: -18, text: 'Already booked within 3 days of this gap' });
  } else if (upcomingInWindow.length === 0) {
    score += 14;
    evidence.push({ kind: 'calendar-open', weight: 14, text: 'Calendar wide open for the next 2 weeks' });
  } else if (upcomingInWindow.length === 1) {
    score += 6;
    evidence.push({ kind: 'calendar-open', weight: 6, text: 'Flexible schedule, one booking in the window' });
  }

  return { score, evidence };
}

/**
 * Candidates to fill one gap, sorted into the five-rung fill ladder (board 08),
 * worked left to right:
 *  1) bring-forward — a future booking with this artist; prefer-brought-forward
 *     to the very top, then fitting blocks, then furthest-out first;
 *  2) deposit — a paid deposit with no date, this artist's first then
 *     unassigned, oldest first;
 *  3) past-client (open business) — prior attended work OR an unfinished piece
 *     with this artist (incl. no-rebook mid-piece), by match score;
 *  4) expired — a deposit link that expired / was never paid;
 *  5) no-show — a previous forfeit / no-show (last, worked only as a fallback).
 * Nobody is excluded for being recently tattooed; the deposit + open-business
 * rungs carry a "days since tattoo" flag instead (tv-25a).
 */
function buildGapQueue(args: BuildArgs, filter: GapFilter): QueueItem[] {
  const artist = args.artists[filter.artistId];
  if (!artist) return [];
  const gapDayStart = new Date(filter.dayIso).getTime();
  const now = new Date(args.nowIso).getTime();

  // Index bookings + pieces by customer once so per-customer scoring is O(small)
  // instead of O(total bookings) — the sweeper works thousands of records.
  const bookingsByCustomer = new Map<string, Booking[]>();
  for (const b of Object.values(args.bookings)) {
    const arr = bookingsByCustomer.get(b.customerId);
    if (arr) arr.push(b);
    else bookingsByCustomer.set(b.customerId, [b]);
  }
  const piecesByCustomer = new Map<string, Piece[]>();
  for (const p of Object.values(args.pieces)) {
    const arr = piecesByCustomer.get(p.customerId);
    if (arr) arr.push(p);
    else piecesByCustomer.set(p.customerId, [p]);
  }

  const makeItem = (
    c: CustomerRecord,
    bucket: GapBucket,
    score: number,
    evidence: MatchEvidence[],
    detail: string,
    depositId?: string,
    recentlyTattooedDays?: number,
  ): QueueItem => {
    const rec = recommendChannel(c);
    const topReasons = evidence
      .filter((e) => e.weight > 0)
      .slice(0, 3)
      .map((e) => e.text)
      .join(' · ');
    // Stamp the customer's sequence-clock state so the sweeper can group
    // candidates by First/Second/Third attempt. A candidate with no sequence
    // leaves stage undefined and is treated as First by consumers.
    const seq = args.sequenceState?.[c.id];
    return {
      id: `q-gap-${c.id}`,
      kind: 'gap-candidate',
      band: 'gap',
      customer: c,
      reason: `${score} match`,
      detail,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      priority: score,
      matchScore: score,
      matchReason: topReasons,
      evidence,
      gapBucket: bucket,
      ...(seq?.stage !== undefined ? { stage: seq.stage } : {}),
      ...(seq?.redKind ? { redKind: seq.redKind } : {}),
      // The sequence "lock": mirror the standard queue's cooldown stamp so the
      // Sweeper can flag a candidate still inside its 24h call-sequence cooldown
      // as hands-off (dormant), while the free-again instant stays visible.
      ...(seq?.nextDueAt
        ? { sequenceDueAtIso: seq.nextDueAt, dormant: new Date(seq.nextDueAt).getTime() > now }
        : {}),
      ...(depositId ? { depositId } : {}),
      ...(recentlyTattooedDays !== undefined ? { recentlyTattooedDays } : {}),
    };
  };

  // FIT check for the bring-forward bucket: compare a candidate's future
  // booking length against the gap's largest real free block, grounded the
  // same way GapFillWorkspace.gapTarget is. Non-fitting candidates aren't
  // hidden — they're deprioritised to the bottom of the bucket.
  const gapSchedule = buildDaySchedule({ artist, bookings: args.bookings, dayIso: filter.dayIso });
  const largestGapWindow = [...gapSchedule.freeWindows].sort((a, b) => b.hours - a.hours)[0];
  const gapBlockMin = largestGapWindow ? largestGapWindow.hours * 60 : 0;
  const gapBlockHours = largestGapWindow?.hours ?? 0;

  const bringForward: Array<{ item: QueueItem; bookingMs: number; fits: boolean; prefer: boolean }> = [];
  const deposits: Array<{ item: QueueItem; thisArtist: boolean; depositMs: number }> = [];
  const pastClients: QueueItem[] = [];
  const expiredLinks: QueueItem[] = [];
  const noShows: QueueItem[] = [];

  for (const c of Object.values(args.customers)) {
    if (c.doNotContact) continue;

    const custBookings = bookingsByCustomer.get(c.id) ?? [];
    const custPieces = piecesByCustomer.get(c.id) ?? [];

    // tv-25a: the healing exclusion is gone. Instead compute days since the
    // client's most recent completed tattoo, surfaced as a flag on the deposit +
    // open-business rungs only (no cut-off — the sweeper decides case by case).
    const lastCompletedMs = custBookings
      .filter((b) => b.status === 'completed')
      .reduce((max, b) => Math.max(max, new Date(b.start).getTime()), -Infinity);
    const tattooDays =
      lastCompletedMs > -Infinity ? Math.max(0, Math.round((now - lastCompletedMs) / DAY_MS)) : undefined;

    const { score, evidence } = gapBaseSignals(c, artist, args, gapDayStart, custBookings, custPieces);

    // Rung 1 — booked ahead with this artist. Ordered by booking date later.
    const futureWithArtist = custBookings
      .filter(
        (b) =>
          b.artistId === artist.id &&
          b.status !== 'cancelled' &&
          b.status !== 'no-show' &&
          b.status !== 'superseded',
      )
      // Strictly a later day than the gap — a booking already on the gap day
      // (e.g. one just moved in) is no longer something to bring forward.
      .filter((b) => new Date(b.start).getTime() >= gapDayStart + DAY_MS)
      .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
    if (futureWithArtist.length > 0) {
      const ahead = futureWithArtist[0]!;
      const fits = ahead.durationMin <= gapBlockMin;
      const prefer = ahead.preferBroughtForward === true;
      const fitEvidence: MatchEvidence = fits
        ? { kind: 'calendar-open', weight: 8, text: `Booked ${ahead.sessionLength} fits this ${gapBlockHours}h gap` }
        : { kind: 'calendar-busy', weight: 0, text: `Booked ${ahead.sessionLength} won't fit this ${gapBlockHours}h gap — would need to split` };
      const preferEvidence: MatchEvidence[] = prefer
        ? [{ kind: 'future-signal', weight: 15, text: 'Asked to be brought forward if a slot opens' }]
        : [];
      const bfItem = makeItem(
        c,
        'bring-forward',
        score,
        [...evidence, fitEvidence, ...preferEvidence],
        `${prefer ? 'Wants to be brought forward · ' : ''}Booked ${gapShortDate(ahead.start)}, could jump the line`,
      );
      // Surface the flag + booking date on the item so the Sweeper row can badge
      // a prefer-brought-forward candidate and order the non-preferred remainder
      // (tv-dme). The prefer-first sort itself stays automatic (see below).
      if (prefer) bfItem.preferBroughtForward = true;
      bfItem.bringForwardBookingIso = ahead.start;
      bringForward.push({
        item: bfItem,
        bookingMs: new Date(ahead.start).getTime(),
        fits,
        prefer,
      });
      continue;
    }

    // Rung 2 — paid deposit on file, no date locked in.
    const openDeposits = c.deposits
      .map((id) => args.deposits[id])
      .filter((d): d is NonNullable<typeof d> => Boolean(d))
      .filter((d) => d.status === 'paid' && !d.bookingId);
    if (openDeposits.length > 0) {
      const depMs = (d: Deposit) => new Date(d.paidAt ?? d.createdAt).getTime();
      const dep = [...openDeposits].sort((a, b) => depMs(a) - depMs(b))[0]!;
      const ageDays = Math.max(0, Math.round((now - depMs(dep)) / DAY_MS));
      const thisArtist = dep.artistId === artist.id;
      const w = Math.min(30, ageDays);
      const depEvidence: MatchEvidence[] = [
        ...evidence,
        {
          kind: 'deposit-age',
          weight: w,
          text: `Deposit ${ageDays}d old${thisArtist ? ` with ${artist.firstName}` : ' (unassigned)'} — convert it`,
        },
      ];
      deposits.push({
        item: makeItem(c, 'deposit', score + w, depEvidence, `$${dep.amountAud} deposit, ${ageDays}d old, no date set`, dep.id, tattooDays),
        thisArtist,
        depositMs: depMs(dep),
      });
      continue;
    }

    // Rung 3 — open / unfinished business: prior attended work with this artist,
    // OR an unfinished piece assigned to them (catches no-rebook mid-piece).
    const hasPriorWorkWithArtist = custBookings.some(
      (b) =>
        b.artistId === artist.id &&
        b.status !== 'cancelled' &&
        b.status !== 'no-show' &&
        b.status !== 'superseded',
    );
    const hasOpenPieceWithArtist = custPieces.some(
      (p) =>
        p.assignedArtistId === artist.id &&
        (p.status === 'in-progress' || p.status === 'paused-will-return' || p.status === 'planning'),
    );
    if (hasPriorWorkWithArtist || hasOpenPieceWithArtist) {
      let cScore = score;
      const cEvidence = [...evidence];
      const lastMs = lastContactMs(c);
      if (lastMs !== null) {
        const days = Math.max(0, Math.round((now - lastMs) / DAY_MS));
        if (days >= 7) {
          const w = Math.min(20, Math.round(days / 7) * 4);
          cScore += w;
          cEvidence.push({ kind: 'days-since-contact', weight: w, text: `${days}d since last contact — cold, worth a nudge` });
        }
      }
      const stage = args.sequenceState?.[c.id]?.stage;
      if (typeof stage === 'number') {
        const w = (4 - stage) * 3;
        if (w > 0) {
          cScore += w;
          cEvidence.push({ kind: 'sequence', weight: w, text: `Sequence stage ${stage} — earlier stage, not yet chased hard` });
        }
      }
      const topReasons = cEvidence.filter((e) => e.weight > 0).slice(0, 3).map((e) => e.text).join(' · ');
      pastClients.push(
        makeItem(c, 'past-client', cScore, cEvidence, topReasons || 'Open business', undefined, tattooDays),
      );
      continue;
    }

    // Rung 4 — expired links & never-paid: a deposit link that expired.
    // Self-book (artist-link) expired deposits are EXCLUDED (tv-311): an expired
    // self-book link is the Agent's follow-up as a hot lead, not a Sweeper gap
    // item (reconcileSelfBookLinkExpiry routes them). Regular expired deposit
    // links keep their place here.
    const hasExpiredLink = c.deposits
      .map((id) => args.deposits[id])
      .some((d) => d?.status === 'expired' && d.source !== 'artist-link');
    if (hasExpiredLink) {
      expiredLinks.push(makeItem(c, 'expired', score, evidence, 'Deposit link expired, never paid — worth another try'));
      continue;
    }

    // Rung 5 — forfeits & no-shows (last, worked only when nothing above fills).
    const hasNoShow = custBookings.some((b) => b.status === 'no-show');
    if (hasNoShow) {
      noShows.push(makeItem(c, 'no-show', score, evidence, 'Previous no-show / forfeit'));
    }
  }

  bringForward.sort((a, b) => {
    if (a.prefer !== b.prefer) return a.prefer ? -1 : 1; // prefer-brought-forward to the very top
    if (a.fits !== b.fits) return a.fits ? -1 : 1; // fitting block next
    return b.bookingMs - a.bookingMs; // furthest-out first within each group
  });
  deposits.sort((a, b) => {
    if (a.thisArtist !== b.thisArtist) return a.thisArtist ? -1 : 1;
    return a.depositMs - b.depositMs; // oldest first
  });
  pastClients.sort((a, b) => b.priority - a.priority);
  expiredLinks.sort((a, b) => b.priority - a.priority);
  noShows.sort((a, b) => b.priority - a.priority);

  return [
    ...bringForward.map((x) => x.item),
    ...deposits.map((x) => x.item),
    ...pastClients,
    ...expiredLinks,
    ...noShows,
  ];
}

function castChannel(c: string): QueueChannel {
  if (c === 'voice' || c === 'sms' || c === 'email' || c === 'meta-dm' || c === 'ig-dm') return c;
  return 'sms';
}

function labelChannel(c: QueueChannel): string {
  if (c === 'voice') return 'voice';
  if (c === 'sms') return 'SMS';
  if (c === 'meta-dm') return 'Messenger';
  if (c === 'ig-dm') return 'IG DM';
  return 'email';
}

function lastMatching<T>(arr: T[], pred: (x: T) => boolean): T | undefined {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (pred(arr[i]!)) return arr[i];
  }
  return undefined;
}

function formatHours(ms: number): string {
  if (ms < HOUR) return `${Math.max(1, Math.round(ms / 60_000))}m`;
  if (ms < 48 * HOUR) return `${Math.round(ms / HOUR)}h`;
  return `${Math.round(ms / (24 * HOUR))}d`;
}

// Common context bullets appended to every standard queue item so the "Why
// this matters" panel has substance for all kinds, not just gap candidates.
function contextEvidence(
  customer: CustomerRecord,
  primary: MatchEvidence[],
): MatchEvidence[] {
  const out: MatchEvidence[] = [...primary];
  if (customer.lifetimeSpendAud >= 5000) {
    out.push({
      kind: 'spend-tier',
      weight: 20,
      text: `VIP: $${customer.lifetimeSpendAud.toLocaleString()} lifetime spend`,
    });
  } else if (customer.lifetimeSpendAud >= 2000) {
    out.push({
      kind: 'spend-tier',
      weight: 12,
      text: `High value: $${customer.lifetimeSpendAud.toLocaleString()} lifetime`,
    });
  } else if (customer.lifetimeSpendAud >= 500) {
    out.push({
      kind: 'spend-tier',
      weight: 6,
      text: `Returning: $${customer.lifetimeSpendAud.toLocaleString()} lifetime`,
    });
  } else if (customer.lifetimeSpendAud === 0) {
    out.push({
      kind: 'spend-tier',
      weight: 4,
      text: 'Brand-new lead, no prior spend',
    });
  }
  if (customer.distanceToNearestStudioKm > 0) {
    out.push({
      kind: 'proximity',
      weight: customer.distanceToNearestStudioKm <= 5 ? 6 : 2,
      text: `${customer.distanceToNearestStudioKm.toFixed(1)} km from nearest studio · ${customer.suburb}`,
    });
  }
  if (customer.engagementStatus === 'lapsed') {
    out.push({
      kind: 'engagement',
      weight: 5,
      text: 'Lapsed, hasn\'t been in for a while',
    });
  } else if (customer.engagementStatus === 'dormant') {
    out.push({
      kind: 'engagement',
      weight: 8,
      text: 'Dormant, long overdue',
    });
  }
  return out;
}

export interface HeatmapCell {
  artistId: string;
  dayIso: string;
  dayOfWeek: DayOfWeek;
  freeHours: number;
  workingHours: number;
}

export interface HeatmapRow {
  artist: Artist;
  cells: HeatmapCell[];
}

const DOW: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export function buildHeatmap(args: {
  artists: Record<string, Artist>;
  bookings: Record<string, Booking>;
  nowIso: string;
  studioIds: StudioId[];
  /** Window start (day-normalised). Defaults to nowIso so existing callers are unchanged. */
  startIso?: string;
  days?: number;
}): HeatmapRow[] {
  const days = args.days ?? 14;
  const allow = new Set(args.studioIds);
  const inStudio = (id: StudioId) => allow.size === 0 || allow.has(id);
  const start = new Date(args.startIso ?? args.nowIso);
  start.setHours(0, 0, 0, 0);

  const artistList = Object.values(args.artists)
    .filter((a) => inStudio(a.studioId))
    .filter((a) => !a.isApprentice)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  return artistList.map((artist) => {
    const cells: HeatmapCell[] = [];
    for (let i = 0; i < days; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      const dow = DOW[day.getDay()]!;
      const window = artist.availability.pattern[dow];
      if (!window) {
        cells.push({ artistId: artist.id, dayIso: day.toISOString(), dayOfWeek: dow, freeHours: 0, workingHours: 0 });
        continue;
      }
      const winStart = parseTime(window.startTime, day);
      const winEnd = parseTime(window.endTime, day);
      const workingHours = Math.max(0, (winEnd - winStart) / HOUR);
      const dayStart = day.getTime();
      const dayEnd = dayStart + 24 * HOUR;
      const realUsed = Object.values(args.bookings)
        .filter((b) => b.artistId === artist.id)
        .filter((b) => {
          if (b.status === 'cancelled' || b.status === 'no-show' || b.status === 'superseded')
            return false;
          const t = new Date(b.start).getTime();
          return t >= dayStart && t < dayEnd;
        })
        .reduce((sum, b) => sum + b.durationMin / 60, 0);
      const syntheticUsed = workingHours * targetUtilisation(artist.id, day.toISOString());
      const usedHours = Math.max(realUsed, syntheticUsed);
      const freeHours = Math.max(0, Math.round(workingHours - usedHours));
      cells.push({
        artistId: artist.id,
        dayIso: day.toISOString(),
        dayOfWeek: dow,
        freeHours,
        workingHours,
      });
    }
    return { artist, cells };
  });
}

export type GapWeekWindow = 'current' | 'next';
export type GapUrgency = 'today' | 'tomorrow' | 'week';

export interface GapListRow {
  id: string;
  artist: Artist;
  studioId: StudioId;
  dayIso: string;
  /** Total open hours that day, fragmented windows summed into one figure. */
  openHours: number;
  /** Rostered hours that day, from availability.pattern. */
  rosteredHours: number;
  /** Tier 1 = near-full (>=90% rostered) or 4h+; Tier 2 = smaller part days. */
  tier: 1 | 2;
  urgency: GapUrgency;
}

// Below this many open hours a day isn't worth surfacing as a gap. Tunable.
const MIN_GAP_HOURS = 2;
// How many weeks forward the "next week onwards" window spans. Tunable.
const NEXT_WINDOW_WEEKS = 4;

function startOfLocalDay(ms: number): Date {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d;
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Window bounds for a gap surface. 'current' = today→end of this week (Mon–Sun);
 * 'next' = from next Monday spanning NEXT_WINDOW_WEEKS weeks. */
function gapWindow(nowIso: string, weekWindow: GapWeekWindow): { start: Date; days: number } {
  const today = startOfLocalDay(new Date(nowIso).getTime());
  const dow = today.getDay(); // 0 Sun .. 6 Sat
  if (weekWindow === 'current') {
    const daysLeft = dow === 0 ? 1 : 8 - dow;
    return { start: today, days: daysLeft };
  }
  const rawToMonday = (1 - dow + 7) % 7;
  const toNextMonday = rawToMonday === 0 ? 7 : rawToMonday;
  const start = new Date(today);
  start.setDate(today.getDate() + toNextMonday);
  return { start, days: NEXT_WINDOW_WEEKS * 7 };
}

/**
 * Ordered list of open-hour gaps for a set of studios, one row per artist/day,
 * fragmented windows summed. Built from availability minus REAL bookings (not
 * the heatmap's synthetic utilisation) so every row is a genuinely fillable
 * slab. Ordering is opportunity-size first, tempered by urgency: Tier 1 rows
 * (near-full or 4h+) soonest-first sit above all Tier 2 rows (part days), which
 * sort by open hours descending then soonest. One flat list across all studios.
 */
export function buildGapList(args: {
  artists: Record<string, Artist>;
  bookings: Record<string, Booking>;
  nowIso: string;
  studioIds: StudioId[];
  weekWindow: GapWeekWindow;
}): GapListRow[] {
  const allow = new Set(args.studioIds);
  const inStudio = (id: StudioId) => allow.size === 0 || allow.has(id);
  const { start, days } = gapWindow(args.nowIso, args.weekWindow);
  const nowMs = new Date(args.nowIso).getTime();
  const todayYmd = ymd(startOfLocalDay(nowMs));
  const tomorrow = startOfLocalDay(nowMs);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowYmd = ymd(tomorrow);

  const artistList = Object.values(args.artists)
    .filter((a) => inStudio(a.studioId))
    .filter((a) => !a.isApprentice && !a.archived);

  // Index live bookings by artist once so each cell scans a small per-artist
  // list, not the whole booking table.
  const bookingsByArtist = new Map<string, Booking[]>();
  for (const b of Object.values(args.bookings)) {
    if (b.status === 'cancelled' || b.status === 'no-show' || b.status === 'superseded') continue;
    const arr = bookingsByArtist.get(b.artistId);
    if (arr) arr.push(b);
    else bookingsByArtist.set(b.artistId, [b]);
  }

  const rows: GapListRow[] = [];
  for (const artist of artistList) {
    const leaveRanges = artist.availability.leaveBlocks.map((lb) => ({ from: lb.startDate, to: lb.endDate }));
    for (let i = 0; i < days; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      const dayYmd = ymd(day);
      const dow = DOW[day.getDay()]!;
      const window = artist.availability.pattern[dow];
      if (!window) continue;
      if (artist.availability.rdoDates.includes(dayYmd)) continue;
      if (leaveRanges.some((r) => dayYmd >= r.from && dayYmd <= r.to)) continue;

      const winStart = parseTime(window.startTime, day);
      const winEnd = parseTime(window.endTime, day);
      const rosteredHours = Math.max(0, (winEnd - winStart) / HOUR);
      if (rosteredHours <= 0) continue;

      const dayStart = day.getTime();
      const dayEnd = dayStart + 24 * HOUR;
      const realUsed = (bookingsByArtist.get(artist.id) ?? [])
        .filter((b) => {
          const t = new Date(b.start).getTime();
          return t >= dayStart && t < dayEnd;
        })
        .reduce((sum, b) => sum + b.durationMin / 60, 0);

      // Displayed open hours come from REAL bookings (fragmented windows summed),
      // so filling or vacating a slot moves the number immediately.
      const openHours = Math.max(0, Math.round(rosteredHours - realUsed));

      // Occupancy gate: only surface days the heatmap also shows as open, using
      // the same real-vs-synthetic-utilisation model, so the sweeper reflects the
      // same busy studios and stays light rather than listing every empty roster.
      const syntheticUsed = rosteredHours * targetUtilisation(artist.id, day.toISOString());
      const occupancyOpen = Math.max(0, Math.round(rosteredHours - Math.max(realUsed, syntheticUsed)));
      if (occupancyOpen < MIN_GAP_HOURS) continue;

      const tier: 1 | 2 = openHours / rosteredHours >= 0.9 || openHours >= 4 ? 1 : 2;
      const urgency: GapUrgency =
        dayYmd === todayYmd ? 'today' : dayYmd === tomorrowYmd ? 'tomorrow' : 'week';

      rows.push({
        id: `${artist.id}-${dayYmd}`,
        artist,
        studioId: artist.studioId,
        dayIso: day.toISOString(),
        openHours,
        rosteredHours: Math.round(rosteredHours),
        tier,
        urgency,
      });
    }
  }

  rows.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier; // Tier 1 above Tier 2
    if (a.tier === 1) return new Date(a.dayIso).getTime() - new Date(b.dayIso).getTime();
    if (a.openHours !== b.openHours) return b.openHours - a.openHours; // Tier 2: hours desc
    return new Date(a.dayIso).getTime() - new Date(b.dayIso).getTime(); // then soonest
  });

  return rows;
}

function parseTime(hhmm: string, baseDate: Date): number {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  const d = new Date(baseDate);
  d.setHours(h ?? 0, m ?? 0, 0, 0);
  return d.getTime();
}

export interface DaySchedule {
  artistId: string;
  dayIso: string;
  working: { startHour: number; endHour: number } | null;
  bookings: Array<{
    id: string;
    startHour: number;
    endHour: number;
    customerId: string;
    designBrief: string;
  }>;
  freeWindows: Array<{ startHour: number; endHour: number; hours: number }>;
}

export function buildDaySchedule(args: {
  artist: Artist;
  bookings: Record<string, Booking>;
  dayIso: string;
}): DaySchedule {
  const day = new Date(args.dayIso);
  day.setHours(0, 0, 0, 0);
  const dow = DOW[day.getDay()]!;
  const window = args.artist.availability.pattern[dow];
  if (!window) {
    return {
      artistId: args.artist.id,
      dayIso: day.toISOString(),
      working: null,
      bookings: [],
      freeWindows: [],
    };
  }

  const startHour = hourOf(window.startTime);
  const endHour = hourOf(window.endTime);
  const dayStart = day.getTime();
  const dayEnd = dayStart + 24 * HOUR;

  const dayBookings = Object.values(args.bookings)
    .filter((b) => b.artistId === args.artist.id)
    .filter((b) => b.status !== 'cancelled' && b.status !== 'no-show' && b.status !== 'superseded')
    .filter((b) => {
      const t = new Date(b.start).getTime();
      return t >= dayStart && t < dayEnd;
    })
    .map((b) => {
      const bStart = new Date(b.start);
      const sh = bStart.getHours() + bStart.getMinutes() / 60;
      return {
        id: b.id,
        startHour: sh,
        endHour: sh + b.durationMin / 60,
        customerId: b.customerId,
        designBrief: b.designBrief,
      };
    })
    .sort((a, b) => a.startHour - b.startHour);

  let freeWindows: DaySchedule['freeWindows'] = [];
  let cursor = startHour;
  for (const b of dayBookings) {
    if (b.startHour > cursor) {
      freeWindows.push({
        startHour: cursor,
        endHour: b.startHour,
        hours: Math.round((b.startHour - cursor) * 10) / 10,
      });
    }
    cursor = Math.max(cursor, b.endHour);
  }
  if (cursor < endHour) {
    freeWindows.push({
      startHour: cursor,
      endHour,
      hours: Math.round((endHour - cursor) * 10) / 10,
    });
  }

  // Synthesise filler blocks so the day reads consistent with the heatmap
  // and reflects realistic studio utilisation. Filler absorbs from the LONGEST
  // free windows first so the single largest gap remains visible to fill.
  const workingHours = endHour - startHour;
  const realUsed = dayBookings.reduce((s, b) => s + (b.endHour - b.startHour), 0);
  const targetUsed = workingHours * targetUtilisation(args.artist.id, day.toISOString());
  let toFill = Math.max(0, targetUsed - realUsed);
  const fillerBlocks: DaySchedule['bookings'] = [];

  if (toFill > 0 && freeWindows.length > 0) {
    const sortable = freeWindows
      .map((w, i) => ({ idx: i, w: { ...w } }))
      .sort((a, b) => b.w.hours - a.w.hours);
    // Skip the largest window so the agent always sees the headline gap.
    const queue = sortable.slice(1);
    let synthCounter = 0;
    for (const entry of queue) {
      if (toFill <= 0.05) break;
      const absorb = Math.min(entry.w.hours, toFill);
      if (absorb <= 0) continue;
      fillerBlocks.push({
        id: `synth-${args.artist.id}-${day.toISOString()}-${synthCounter++}`,
        startHour: entry.w.startHour,
        endHour: entry.w.startHour + absorb,
        customerId: '',
        designBrief: 'Studio time',
      });
      entry.w.startHour += absorb;
      entry.w.hours -= absorb;
      freeWindows[entry.idx] = entry.w;
      toFill -= absorb;
    }
    // Also shave the front of the largest window if there's still synth to add.
    if (toFill > 0.05) {
      const largest = sortable[0];
      if (largest) {
        const absorb = Math.min(largest.w.hours - 0.5, toFill);
        if (absorb > 0) {
          fillerBlocks.push({
            id: `synth-${args.artist.id}-${day.toISOString()}-${synthCounter++}`,
            startHour: largest.w.startHour,
            endHour: largest.w.startHour + absorb,
            customerId: '',
            designBrief: 'Studio time',
          });
          freeWindows[largest.idx] = {
            startHour: largest.w.startHour + absorb,
            endHour: largest.w.endHour,
            hours: Math.round((largest.w.hours - absorb) * 10) / 10,
          };
        }
      }
    }
  }

  freeWindows = freeWindows.filter((w) => w.hours >= 0.5);
  const allBookings = [...dayBookings, ...fillerBlocks].sort(
    (a, b) => a.startHour - b.startHour,
  );

  return {
    artistId: args.artist.id,
    dayIso: day.toISOString(),
    working: { startHour, endHour },
    bookings: allBookings,
    freeWindows,
  };
}

function hourOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  return (h ?? 0) + (m ?? 0) / 60;
}

// Deterministic hash for fixture-realistic utilisation. We don't seed real
// fixture bookings densely enough for the prototype's "most artists booked
// solid" story, so we layer a synthetic load on top to drive the 80/10/10
// distribution. Output is in [0, 1).
function dayHash(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h) / 2147483647;
}

// Returns target utilisation in [0, 1]. 80% of artist-days hit 0.85-1.0 (solid
// or near-solid), 10% hit 0.5-0.85 (partial), 10% hit 0.1-0.5 (wide-open gap).
export function targetUtilisation(artistId: string, dayIso: string): number {
  const h = dayHash(`${artistId}|${dayIso.slice(0, 10)}`);
  if (h < 0.8) return 0.85 + (h / 0.8) * 0.15;
  if (h < 0.9) return 0.5 + ((h - 0.8) / 0.1) * 0.35;
  return 0.1 + ((h - 0.9) / 0.1) * 0.4;
}
